// @flow

import {extend, bindAll} from '../../util/util';
import type Map from '../map';
import {FilterControlBase} from './filter_control_base';
import WeatherAlertLegendControl from './weatheralert_legend_control';
import type {Options} from './filter_control_base';
import window from '../../util/window';
type WeatherAlertOptions = Options & {
    severity: ?string,
    urgency: ?string,
    certainty: ?string,
    eventNames: ?Array<string>
};

const defaultOptions: WeatherAlertOptions = {
    id: 'weatheralert',
    loadingEventId: 'weatheralert',
    isVisible: true,
    severity: 'All',
    urgency: 'All',
    certainty: 'All',
    eventNames: null
};

/**
 * The `WeatherAlertFilterControl` adds the ability to filter the weather alerts.
 *
 * @implements {IControl}
 * @param {Object} options
 * @param {string} [options.severity='All'] Filters results based on the alert severity ("All", "Extreme", "Severe", "Moderate", "Minor", "Unknown").
 * @param {string} [options.urgency='All'] Filters results based on the alert urgency ("All", "Immediate", "Expected", "Future", "Past", "Unknown").
 * @param {string} [options.certainty='All'] Filters results based on the alert certainty ("All", "Observed", "Likely", "Possible", "Unlikely", "Unknown").
 * @param {Array} options.eventNames Array of event names such as ["Winter Storm Warning", "Flood Watch"].
 * @param {boolean} [options.isVisible=true] Controls the visibility of the selection controls.
 *
 * @example
 * const weatherAlertFilterControl = TrimbleMaps.WeatherAlertFilterControl();
 * map.addControl(weatherAlertFilterControl, 'top-left');
 */
class WeatherAlertFilterControl extends FilterControlBase {
    _eventNameFilter: ?Array<string>;
    _isVisible: ?boolean;
    _childControl: ?any;

    constructor(options: WeatherAlertOptions) {
        const opt = extend({}, defaultOptions, options);
        super(opt);
        const severityValues = ['All', 'Extreme', 'Severe', 'Moderate', 'Minor', 'Unknown'];
        const urgencyValues = ['All', 'Immediate', 'Expected', 'Future', 'Past', 'Unknown'];
        const certaintyValues = ['All', 'Observed', 'Likely', 'Possible', 'Unlikely', 'Unknown'];
        let severityDefaultFilter = severityValues[0];
        let urgencyDefaultFilter = urgencyValues[0];
        let certaintyDefaultFilter = certaintyValues[0];
        if (severityValues.indexOf(opt.severity) > 0) {
            severityDefaultFilter = opt.severity;
        }
        if (urgencyValues.indexOf(opt.urgency) > 0) {
            urgencyDefaultFilter = opt.urgency;
        }
        if (certaintyValues.indexOf(opt.certainty) > 0) {
            certaintyDefaultFilter = opt.certainty;
        }
        this._eventNameFilter = opt.eventNames;
        this._isVisible = opt.isVisible;
        this._layers = ['weatheralert-weatheralert_outline', 'weatheralert-weatheralert_fill'];
        this._filterTitle = 'Weather Alert';
        this._filterObjects = [
            {
                id: 'severity',
                label: 'Severity',
                texts: ['All', 'Extreme', 'Severe', 'Moderate', 'Minor', 'Unknown'],
                values: severityValues,
                default: severityDefaultFilter
            },
            {
                id: 'urgency',
                label: 'Urgency',
                texts: ['All', 'Immediate', 'Expected', 'Future', 'Past', 'Unknown'],
                values: urgencyValues,
                default: urgencyDefaultFilter
            },
            {
                id: 'certainty',
                label: 'Certainty',
                texts: ['All', 'Observed', 'Likely (>50%)', 'Possible (<=50%)', 'Unlikely (0)', 'Unknown'],
                values: certaintyValues,
                default: certaintyDefaultFilter
            }
        ];
        this._containerClass = 'trimblemaps-ctrl-filter-container';
        this._headerClass = 'trimblemaps-ctrl-accordion';
        bindAll([
            '_onLoad',
            '_onSourceData'
        ], this);
    }

    onAdd(map: Map) {
        const container = super.onAdd(map);
        this._map.on('weatheralert', () => {
            const weatheralertcontrol = window.document.querySelector('#weatheralert');
            const weatherlegendcontrol = window.document.querySelector('#weatherlegendcontainer');
            if (weatheralertcontrol && !weatheralertcontrol.contains(weatherlegendcontrol)) {
                this._childControl = new WeatherAlertLegendControl({id: 'weatheralertslegend'});
                container.appendChild(this._childControl.onAdd(map));
            }
        });
        this._map.on('load', this._onLoad);
        return container;
    }

    onRemove() {
        this._map.off('sourcedata', this._onSourceData);
        this._map.off('load', this._onLoad);
        super.onRemove();
    }

    /**
     * Gets filter.
     * @returns {Array<any>} Filters
     */
    getFilter() { // The legend control depends on this method.
        return super._getFilter();
    }

    _getFilter() {
        const filter: any[] = this.getFilter();
        if (this._eventNameFilter && Array.isArray(this._eventNameFilter) && this._eventNameFilter.length > 0) {
            filter.push(['in', 'eventName', ...this._eventNameFilter]);
        }
        return filter;
    }

    _onLoad() {
        super.filter();
        this._map.on('sourcedata', this._onSourceData);
    }

    _onSourceData(e: any) {
        if ((e.sourceId === 'WeatherAlerts') && (!e.sourceDataType) && (this._isVisible)) {
            this._container.style.display = 'block';
        }
    }
}

export default WeatherAlertFilterControl;
